import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const next = new URLSearchParams(loc.search).get("next") || "/admin";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [mfaCode, setMfaCode] = useState("");
    const [mfaRequired, setMfaRequired] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    async function onSubmit(e) {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const res = await login(email, password, mfaCode || undefined);
            if (res.mfa_required) {
                setMfaRequired(true);
                toast.info("Enter your authenticator code");
                return;
            }
            toast.success("Welcome back");
            nav(next, { replace: true });
        } catch (err) {
            const msg = formatApiError(err);
            setError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen bg-[color:var(--gsf-green-900)] text-white flex items-center justify-center px-4" data-testid="admin-login">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-8">
                    <Logo variant="full" size={110} className="rounded-xl bg-white p-2" />
                </div>
                <div className="bg-white text-[color:var(--gsf-charcoal)] rounded-2xl p-8 shadow-2xl">
                    <div className="gsf-eyebrow">Administrator Login</div>
                    <h1 className="mt-2 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold">Sign in to continue</h1>
                    <form onSubmit={onSubmit} className="mt-6 space-y-4">
                        <div>
                            <label className="text-sm font-medium">Email</label>
                            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email"
                                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-3 focus:outline-none focus:border-[color:var(--gsf-green-700)]" />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Password</label>
                            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password"
                                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-3 focus:outline-none focus:border-[color:var(--gsf-green-700)]" />
                        </div>
                        {mfaRequired && (
                            <div>
                                <label className="text-sm font-medium">Authenticator code</label>
                                <input inputMode="numeric" required value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} data-testid="admin-login-mfa"
                                    className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-3 tracking-widest text-center focus:outline-none focus:border-[color:var(--gsf-green-700)]" />
                            </div>
                        )}
                        {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
                        <button type="submit" disabled={busy} className="gsf-btn-primary w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">
                            {busy ? "Signing in…" : mfaRequired ? "Verify & Sign in" : "Sign in"}
                        </button>
                    </form>
                    <div className="mt-4 text-sm flex justify-between">
                        <Link to="/admin/forgot-password" className="text-[color:var(--gsf-green-700)] hover:underline" data-testid="admin-forgot-link">Forgot password?</Link>
                        <Link to="/" className="text-gray-500 hover:underline" data-testid="admin-back-home">Back to site</Link>
                    </div>
                </div>
                <p className="text-center text-xs text-white/50 mt-6">
                    Administrator access is protected by rate limiting, audit logging and optional multi-factor authentication.
                </p>
            </div>
        </div>
    );
}
